import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Mail, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDateTime, formatError } from "@/lib/api";

const ICONS = { note: StickyNote, email: Mail, change: History };
const EMAIL_STYLE = { sent: "text-emerald-700", failed: "text-red-600", blocked: "text-slate-900 font-semibold" };

function Item({ it }) {
  const Icon = ICONS[it.kind];
  return (
    <li className="flex gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`activity-${it.kind}-${it.id}`}>
      <span className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Icon className="h-3.5 w-3.5 text-slate-500" /></span>
      <div className="min-w-0 text-sm">
        {it.kind === "note" && <p className="text-slate-800 whitespace-pre-wrap">{it.text}</p>}
        {it.kind === "email" && (
          <p className="text-slate-800">
            <span className={EMAIL_STYLE[it.status]}>[{it.status}]</span> {it.subject}
            <span className="block text-xs text-slate-500">To {it.original_to.join(", ")}{it.safe_mode && it.actual_to.length ? ` → delivered to ${it.actual_to.join(", ")}` : ""}{it.error ? ` · ${it.error}` : ""}</span>
          </p>
        )}
        {it.kind === "change" && (
          <p className="text-slate-700"><span className="font-mono text-xs uppercase text-slate-500">{it.action}</span> {it.collection.replace("_", " ")}{it.fields.length ? `: ${it.fields.join(", ")}` : ""}</p>
        )}
        <p className="text-[11px] text-slate-400 mt-0.5">{it.by || "system"} · {fmtDateTime(it.at)}</p>
      </div>
    </li>
  );
}

export function ActivityFeed({ url, contractId, personId, reloadKey }) {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [kind, setKind] = useState("all");
  const load = useCallback(() => api.get(url).then((r) => setItems(r.data)), [url]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const add = async () => {
    try { await api.post("/notes", { body: note, contract_id: contractId, person_id: personId }); setNote(""); toast.success("Note added"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const shown = kind === "all" ? items : items.filter((i) => i.kind === kind);
  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="flex flex-col gap-2">
          <Textarea placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} className="bg-white" data-testid="note-input" />
          <Button size="sm" className="self-end" disabled={!note.trim()} onClick={add} data-testid="add-note-button">Add note</Button>
        </div>
      )}
      <div className="flex gap-2">
        {["all", "note", "email", "change"].map((k) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`activity-filter-${k}`}
            className={`text-xs rounded-full px-3 py-1 border ${kind === k ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-slate-200 text-slate-500"}`}>
            {{ all: "All", note: "Notes", email: "Emails", change: "Changes" }[k]}
          </button>
        ))}
      </div>
      <ul className="bg-white border border-slate-200 rounded-lg px-4" data-testid="activity-feed">
        {shown.map((it) => <Item key={`${it.kind}-${it.id}`} it={it} />)}
        {!shown.length && <li className="py-6 text-center text-sm text-slate-400">Nothing yet</li>}
      </ul>
    </div>
  );
}
